import {
  getPaginationRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  PaginationState,
  getCoreRowModel,
  useReactTable,
  SortingState,
  TableState,
  Row,
} from '@ui/components';
import { ColumnDef } from '@ui/components';
import { useState, useMemo } from 'react';

export interface UseTableListProps<T> {
  onStateChange: (state: Partial<TableState>) => void;
  hideColumns?: Partial<Record<string, boolean>>;
  enableRowSelection?: (row: Row<T>) => boolean;
  initialState?: Partial<TableState>;
  getRowId: (row: T) => string;
  columns: ColumnDef<T>[];
  isLoading?: boolean;
  totalItems: number;
  data: T[];
}

export function useTableList<T>({
  enableRowSelection = () => true,
  onStateChange,
  initialState,
  hideColumns,
  totalItems,
  isLoading,
  getRowId,
  columns,
  data,
}: UseTableListProps<T>) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>(
    initialState?.pagination ?? { pageIndex: 0, pageSize: 8 },
  );
  const [globalFilter, setGlobalFilter] = useState(initialState?.globalFilter ?? '');
  const [sorting, setSorting] = useState<SortingState>(initialState?.sorting ?? []);

  const columnVisibility = useMemo(() => {
    return columns.reduce(
      (acc, column) => {
        if (column.id) {
          return { ...acc, [column.id]: !hideColumns?.[column.id] };
        }
        return acc;
      },
      {} as Record<string, boolean>,
    );
  }, [columns, hideColumns]);

  const table = useReactTable({
    data: data ?? [],
    columns,
    state: { pagination, globalFilter, rowSelection, sorting, columnVisibility },
    pageCount: Math.ceil(totalItems / pagination.pageSize),
    rowCount: totalItems,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    enableRowSelection,
    getRowId,
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    onGlobalFilterChange: (updater) => {
      const newGlobalFilter = typeof updater === 'function' ? updater(globalFilter) : updater;
      onStateChange({ globalFilter: newGlobalFilter, pagination, rowSelection, sorting });
      setGlobalFilter(newGlobalFilter);
    },
    onRowSelectionChange: (updater) => {
      const newRowSelection = typeof updater === 'function' ? updater(rowSelection) : updater;
      onStateChange({ globalFilter, pagination, rowSelection: newRowSelection, sorting });
      setRowSelection(newRowSelection);
    },
    onPaginationChange: (updater) => {
      const newPagination = typeof updater === 'function' ? updater(pagination) : updater;
      onStateChange({ globalFilter, pagination: newPagination, rowSelection, sorting });
      setPagination(newPagination);
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      onStateChange({ globalFilter, pagination, rowSelection, sorting: newSorting });
      setSorting(newSorting);
    },
  });

  return {
    isLoading,
    getRowId,
    table,
  };
}

export type UseTableList<T> = ReturnType<typeof useTableList<T>>;
